import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Heart, BookOpen, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '@/components/Navbar';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PatternDetail() {
  const { patternId } = useParams();
  const [pattern, setPattern] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadPattern();
  }, [patternId]);

  const loadPattern = async () => {
    try {
      const response = await axios.get(`${API}/patterns/${patternId}`, {
        withCredentials: true
      });
      setPattern(response.data);
    } catch (error) {
      console.error('Failed to load pattern:', error);
      toast.error('Failed to load pattern');
      navigate('/patterns');
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async () => {
    try {
      await axios.post(`${API}/patterns/${patternId}/like`, {}, { withCredentials: true });
      toast.success('Pattern liked!');
      loadPattern();
    } catch (error) {
      console.error('Failed to like pattern:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
            <p className="mt-4 text-muted-foreground">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!pattern) return null;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Button
          variant="ghost"
          onClick={() => navigate('/patterns')}
          className="mb-6 rounded-lg text-stone-600 hover:text-primary hover:bg-rose-50 font-medium"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Patterns
        </Button>

        <div className="rounded-3xl bg-white border border-stone-100 overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          <div className="bg-gradient-to-br from-rose-100 to-orange-100 p-12 text-center">
            <BookOpen className="h-20 w-20 text-primary mx-auto mb-4" />
          </div>

          <div className="p-8">
            <div className="flex justify-between items-start mb-6">
              <div className="flex-1">
                <h1 className="text-4xl font-heading font-extrabold tracking-tight mb-2">
                  {pattern.name}
                </h1>
                <p className="text-lg text-muted-foreground">by {pattern.creator_name}</p>
              </div>
              <div className="flex items-center space-x-2">
                {pattern.is_public ? (
                  <Eye className="h-6 w-6 text-accent" />
                ) : (
                  <EyeOff className="h-6 w-6 text-muted-foreground" />
                )}
                <Button
                  onClick={handleLike}
                  variant="ghost"
                  className="rounded-full"
                >
                  <Heart className="h-5 w-5 mr-2 text-primary" />
                  {pattern.likes_count}
                </Button>
              </div>
            </div>

            {pattern.description && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-muted-foreground mb-2">Description</h3>
                <p className="text-base text-foreground">{pattern.description}</p>
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="rounded-2xl bg-stone-50 p-4">
                <p className="text-xs text-muted-foreground mb-1">Difficulty</p>
                <p className="font-semibold capitalize">{pattern.difficulty}</p>
              </div>
              {pattern.hook_size && (
                <div className="rounded-2xl bg-stone-50 p-4">
                  <p className="text-xs text-muted-foreground mb-1">Hook/Needle</p>
                  <p className="font-semibold">{pattern.hook_size}</p>
                </div>
              )}
              {pattern.gauge && (
                <div className="rounded-2xl bg-stone-50 p-4">
                  <p className="text-xs text-muted-foreground mb-1">Gauge</p>
                  <p className="font-semibold text-sm">{pattern.gauge}</p>
                </div>
              )}
              {pattern.finished_size && (
                <div className="rounded-2xl bg-stone-50 p-4">
                  <p className="text-xs text-muted-foreground mb-1">Finished Size</p>
                  <p className="font-semibold text-sm">{pattern.finished_size}</p>
                </div>
              )}
            </div>

            {pattern.yarn_requirements && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-muted-foreground mb-2">Yarn Requirements</h3>
                <p className="text-base">{pattern.yarn_requirements}</p>
              </div>
            )}

            <div className="mb-6">
              <h3 className="text-sm font-medium text-muted-foreground mb-3">Instructions</h3>
              <div className="rounded-2xl bg-stone-50 p-6">
                <pre className="whitespace-pre-wrap font-body text-sm leading-relaxed">{pattern.instructions}</pre>
              </div>
            </div>

            {pattern.tags && pattern.tags.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-3">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {pattern.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 rounded-full bg-primary/10 text-primary text-sm">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
